import React, { ReactNode } from "react";
import Image from "next/image";

// Import all your icons
import blockQuestionDuotoneSolid from "@/assets/images/icons/block-question-duotone-solid.svg";
import buildingColumnsDuotoneSolid from "@/assets/images/icons/building-columns-duotone-solid.svg";
import cardsBlankDuotoneSolid from "@/assets/images/icons/cards-blank-duotone-solid.svg";
import chatIcon from "@/assets/images/icons/chat.svg";
import checkVerified2 from "@/assets/images/icons/check-verified-02.svg";
import earthEuropeDuotoneSolid from "@/assets/images/icons/earth-europe-duotone-solid.svg";
import fileShield2 from "@/assets/images/icons/file-shield-02.svg";
import fingerprint3 from "@/assets/images/icons/fingerprint-03.svg";
import fingerprintDuotoneSolid from "@/assets/images/icons/fingerprint-duotone-solid.svg";
import graduationCapDuotoneSolid from "@/assets/images/icons/graduation-cap-duotone-solid.svg";
import graduationHat from "@/assets/images/icons/graduation-hat.svg";
import handHoldingCircleDollarDuotoneSolid from "@/assets/images/icons/hand-holding-circle-dollar-duotone-solid.svg";
import lightbulbOnDuotoneSolid from "@/assets/images/icons/lightbulb-on-duotone-solid.svg";
import listCheckDuotoneSolid from "@/assets/images/icons/list-check-duotone-solid.svg";
import lockDuotoneSolid from "@/assets/images/icons/lock-duotone-solid.svg";
import memoCircleInfoDuotoneSolid from "@/assets/images/icons/memo-circle-info-duotone-solid.svg";
import messageSmileDuotoneSolid from "@/assets/images/icons/message-smile-duotone-solid.svg";
import messagesQuestionDuotoneSolid from "@/assets/images/icons/messages-question-duotone-solid.svg";
import pencilDuotoneSolid from "@/assets/images/icons/pencil-duotone-solid.svg";
import phoneSlashDuotoneSolid from "@/assets/images/icons/phone-slash-duotone-solid.svg";
import shieldHalvedDuotoneSolid from "@/assets/images/icons/shield-halved-duotone-solid.svg";
import shieldTick from "@/assets/images/icons/shield-tick.svg";
import streetViewDuotoneSolid from "@/assets/images/icons/street-view-duotone-solid.svg";
import ufoBeamDuotoneSolid from "@/assets/images/icons/ufo-beam-duotone-solid.svg";
import { TitleHeading } from "@habacuscode/habacus_ui_component_library";



// Icon mapping
const iconMap = {
  'block-question-duotone-solid': blockQuestionDuotoneSolid,
  'building-columns-duotone-solid': buildingColumnsDuotoneSolid,
  'cards-blank-duotone-solid': cardsBlankDuotoneSolid,
  'chat': chatIcon,
  'check-verified-02': checkVerified2,
  'earth-europe-duotone-solid': earthEuropeDuotoneSolid,
  'file-shield-02': fileShield2,
  'fingerprint-03': fingerprint3,
  'fingerprint-duotone-solid': fingerprintDuotoneSolid,
  'graduation-cap-duotone-solid': graduationCapDuotoneSolid,
  'graduation-hat': graduationHat,
  'hand-holding-circle-dollar-duotone-solid': handHoldingCircleDollarDuotoneSolid,
  'lightbulb-on-duotone-solid': lightbulbOnDuotoneSolid,
  'list-check-duotone-solid': listCheckDuotoneSolid,
  'lock-duotone-solid': lockDuotoneSolid,
  'memo-circle-info-duotone-solid': memoCircleInfoDuotoneSolid,
  'message-smile-duotone-solid': messageSmileDuotoneSolid,
  'messages-question-duotone-solid': messagesQuestionDuotoneSolid,
  'pencil-duotone-solid': pencilDuotoneSolid,
  'phone-slash-duotone-solid': phoneSlashDuotoneSolid,
  'shield-halved-duotone-solid': shieldHalvedDuotoneSolid,
  'shield-tick': shieldTick,
  'street-view-duotone-solid': streetViewDuotoneSolid,
  'ufo-beam-duotone-solid': ufoBeamDuotoneSolid,
} as const;

// Type definitions
export type InfoItem = {
  icon?: string; // Optional icon key
  text: Record<string, string>; // Multilingual text
};

export type Info = {
  title?: Record<string, string>;
  titleIcon?: string; // Optional title icon key
  items?: InfoItem[];
  node?: ReactNode;
};

// Generic InfoItem component
interface InfoItemProps {
  icon?: string;
  text: string;
}

const InfoItemComponent: React.FC<InfoItemProps> = ({ icon, text }) => {
  const iconSrc = icon ? iconMap[icon as keyof typeof iconMap] : null;
  
  return (
    <div className="flex items-center mb-6">
      {iconSrc && (
        <div className="shrink-0 mr-4">
          <div className="w-6 h-6 rounded-full flex items-center justify-center">
            <Image src={iconSrc} alt={`${icon} icon`} />
          </div>
        </div>
      )}
      <TitleHeading label={text} size="p" />
    </div>
  );
};

// Generic InfoBox component
interface InfoBoxProps {
  info: Info;
  language?: string; // Default language key
}

const InfoBox: React.FC<InfoBoxProps> = ({ info, language = 'it' }) => {
  const titleIconSrc = info.titleIcon ? iconMap[info.titleIcon as keyof typeof iconMap] : null;

  console.log('*** Generic info box ', info)

  if (info.node) {
    return (<>
      {info.node}
    </>)
  }

  return (
    <div className="bg-white p-3 md:p-8 rounded-2xl max-w-lg">
      {/* Header with optional icon */}
      <div className="flex flex-col items-center mb-8">
        {titleIconSrc && (
          <div className="text-blue-600 mb-4 w-10 h-10">
            <Image src={titleIconSrc} alt="info box header icon" />
          </div>
        )}
        <TitleHeading label={info?.title?.[language] || info?.title?.['it'] || ''} size="h2" className="text-center" />
      </div>

      {/* Render all items from config */}
      {info?.items?.map((item, index) => (
        <InfoItemComponent
          key={index}
          icon={item.icon}
          text={item.text[language] || item.text['it'] || ''}
        />
      ))}
    </div>
  );
};

// Main display component
interface GenericInfoBoxDisplayProps {
  info: Info;
  language?: string;
}

export default function GenericInfoBoxDisplay({ info, language = 'it' }: GenericInfoBoxDisplayProps) {
  console.log('*** INFO passed', info)

  if (!info) return null;

  return (
    <div className="bg-white rounded-2xl w-full max-w-lg border">
      <InfoBox info={info} language={language} />
    </div>
  );
}
